import { KONTAKTVALG } from "../misc/referansevalg";
import { Landmark, Plus } from "lucide-react";
import {
  Form,
  required,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { CREATE_SHORTCUT_ATTR } from "@/components/admin/shortcut-attrs";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { contactOptionText } from "../misc/ContactOption";
import { useInvalidate } from "../misc/useInvalidate";
import type { CrmDataProvider } from "../providers/types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type {
  BrregSearchHit,
  CreateLeadInput,
  DealSource,
  LeadDuplicate,
} from "../types";
import { resolveLeadIntakeSettings } from "./leadIntakeDefaults";
import {
  DEAL_SOURCE_FALLBACK,
  QUICK_LEAD_SOURCES,
  useDealSourceLabel,
} from "./leadSources";
import {
  LeadDuplicatePanel,
  summarizeDuplicates,
  useLeadDuplicates,
} from "./LeadDuplicatePanel";
import { suggestNextStep } from "./nextStep";
import {
  NextStepFields,
  validateNextStep,
  type NextStepDraft,
} from "./NextStepFields";

// Innstillingene for inntaket (Innstillinger → Pipeline). Tomme felt gir
// standardverdiene.
export const useLeadIntakeSettings = () =>
  resolveLeadIntakeSettings(useConfigurationContext().leadIntake);

type LeadFormValues = {
  name?: string;
  phone?: string;
  email?: string;
  company_name?: string;
  org_nr?: string;
  need?: string;
  source?: DealSource;
  referred_by_id?: Identifier | null;
  note?: string;
};

type Picked = {
  contactId?: Identifier | null;
  companyId?: Identifier | null;
  // Fra Brønnøysund: adressen følger med til kunden.
  brreg?: BrregSearchHit | null;
};

const clean = (value?: string | null) => value?.trim() || null;

// Hurtigregistrering av en lead (kap. 6.1, 16.6 og 17.6): C på Pipeline og
// «+» i bunnmenyen på mobil. Duplikatsjekken går mens vi skriver, og
// Brønnøysund fyller inn kunden. Leaden havner i Lead inn med et neste steg.
export const LeadQuickCreate = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Fullskjerm på mobil (kap. 17.6), vanlig dialog ellers. */}
      <DialogContent className="max-h-dvh overflow-y-auto max-sm:inset-0 max-sm:h-dvh max-sm:max-w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 sm:max-h-9/10 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {translate("crm.deals.lead.new_title", { _: "Ny lead" })}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.deals.lead.new_description", {
              _: "Registrer en henvendelse. Den havner i Lead inn.",
            })}
          </DialogDescription>
        </DialogHeader>
        {open && <LeadQuickCreateForm onDone={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
};

const LeadQuickCreateForm = ({ onDone }: { onDone: () => void }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const invalidate = useInvalidate();
  const { identity } = useGetIdentity();
  const { dealStages } = useConfigurationContext();
  const { replyCutoffHour } = useLeadIntakeSettings();
  const inbox = dealStages.find((s) => s.is_inbox) ?? dealStages[0];
  const [draft, setDraft] = useState<NextStepDraft>(() => ({
    ...suggestNextStep(inbox, {
      replyCutoffHour,
      fallbackTitle: translate("crm.deals.lead.default_next", {
        _: "Ring tilbake",
      }),
    }),
    // Telefon er standardkilden: et neste steg av typen Ring passer.
    type: inbox?.next_step_type ?? "call",
  }));
  const [errors, setErrors] = useState<{ title?: string; due_date?: string }>(
    {},
  );
  const [picked, setPicked] = useState<Picked>({});
  const [duplicates, setDuplicates] = useState<LeadDuplicate[]>([]);
  const [saving, setSaving] = useState(false);

  const fail = (error: unknown) =>
    notify((error as Error)?.message || "ra.notification.http_error", {
      type: "error",
    });

  // «Legg til notat i stedet»: henvendelsen legges som notat på den åpne
  // muligheten vi fant, og ingen ny lead lages (kap. 6.5).
  const addNoteInstead = async (
    match: LeadDuplicate,
    values: LeadFormValues,
  ) => {
    const lines = [
      clean(values.note),
      [clean(values.name), clean(values.phone), clean(values.email)]
        .filter(Boolean)
        .join(" · "),
    ].filter(Boolean);
    setSaving(true);
    try {
      await dataProvider.create("deal_notes", {
        data: {
          deal_id: match.id,
          text:
            lines.join("\n") ||
            translate("crm.deals.lead.note_fallback", {
              _: "Ny henvendelse",
            }),
          sales_id: identity?.id ?? null,
          date: new Date().toISOString(),
        },
      });
      notify(
        translate("crm.deals.lead.note_added", {
          _: "Lagt til på eksisterende mulighet: %{title}",
          title: match.label,
        }),
      );
      invalidate("deal_notes", "deals");
      onDone();
    } catch (error) {
      fail(error);
    } finally {
      setSaving(false);
    }
  };

  const submit = async (values: LeadFormValues) => {
    const found = validateNextStep(draft, translate);
    setErrors(found);
    if (Object.keys(found).length) return;
    const summary = summarizeDuplicates(duplicates);
    // Eksisterende kunde: kilden blir mersalg og kundeansvarlig eier
    // (kap. 6.5 og 6.6).
    const customer = summary.customer;
    const companyId = picked.companyId ?? customer?.id ?? null;
    // Kanalen sendes som den er: databasen gjør den til mersalg for en
    // eksisterende kunde og beholder kanalen i kildedetaljen (samtalen
    // logges fortsatt for Telefon).
    const source: DealSource | string = values.source ?? "phone";
    const input: CreateLeadInput = {
      name: values.name!.trim(),
      phone: clean(values.phone),
      email: clean(values.email)?.toLowerCase() ?? null,
      company_name: clean(values.company_name),
      org_nr: clean(values.org_nr)?.replace(/\s/g, "") ?? null,
      website: picked.brreg?.website ?? null,
      address: picked.brreg?.address ?? null,
      zipcode: picked.brreg?.zipcode ?? null,
      city: picked.brreg?.city ?? null,
      need: clean(values.need),
      next_step: draft.title.trim(),
      next_step_type: draft.type,
      next_step_date: draft.due_date,
      source,
      referred_by_id:
        values.source === "referral" ? (values.referred_by_id ?? null) : null,
      note: clean(values.note),
      existing_contact_id: picked.contactId ?? null,
      existing_company_id: companyId,
      owner_id: customer?.sales_id ?? null,
    };
    setSaving(true);
    try {
      await dataProvider.createLead(input);
      notify(
        translate("crm.deals.lead.created", {
          _: "Leaden er registrert i Lead inn",
        }),
        { type: "success" },
      );
      invalidate("deals", "work_items", "contacts", "companies", "deal_notes");
      onDone();
    } catch (error) {
      fail(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      onSubmit={(values: Record<string, unknown>) =>
        submit(values as LeadFormValues)
      }
      defaultValues={{ source: "phone" }}
      className="flex flex-col gap-4"
    >
      <LeadQuickCreateFields
        picked={picked}
        setPicked={setPicked}
        onDuplicates={setDuplicates}
        onAddNote={addNoteInstead}
      />
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">
          {translate("crm.deals.next.box_title", { _: "Neste steg" })}
        </h3>
        <NextStepFields
          idPrefix="lead-next"
          value={draft}
          onChange={setDraft}
          errors={errors}
        />
      </section>
      <TextInput
        source="note"
        label={translate("crm.deals.lead.note", { _: "Notat (valgfritt)" })}
        multiline
        rows={3}
        helperText={false}
      />
      <DialogFooter className="max-sm:sticky max-sm:bottom-0 max-sm:bg-background max-sm:py-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onDone}
          disabled={saving}
        >
          {translate("ra.action.cancel", { _: "Avbryt" })}
        </Button>
        <Button type="submit" disabled={saving} className="max-sm:h-11">
          {translate("crm.deals.lead.save", { _: "Lagre lead" })}
        </Button>
      </DialogFooter>
    </Form>
  );
};

// Feltene som trenger skjemaets verdier: duplikatsjekken, Brønnøysund og
// «Anbefalt av».
const LeadQuickCreateFields = ({
  picked,
  setPicked,
  onDuplicates,
  onAddNote,
}: {
  picked: Picked;
  setPicked: (update: (picked: Picked) => Picked) => void;
  onDuplicates: (matches: LeadDuplicate[]) => void;
  onAddNote: (match: LeadDuplicate, values: LeadFormValues) => void;
}) => {
  const translate = useTranslate();
  const sourceLabel = useDealSourceLabel();
  const { dealCategories } = useConfigurationContext();
  const { getValues, setValue } = useFormContext<LeadFormValues>();
  const [email, phone, companyName, orgNr, source] = useWatch<
    LeadFormValues,
    ["email", "phone", "company_name", "org_nr", "source"]
  >({ name: ["email", "phone", "company_name", "org_nr", "source"] });
  const { matches, hasInput, isPending } = useLeadDuplicates({
    email,
    phone,
    company_name: companyName,
    org_nr: orgNr,
  });
  useEffect(() => onDuplicates(matches), [matches, onDuplicates]);

  const sourceChoices = useMemo(
    () =>
      QUICK_LEAD_SOURCES.map((value) => ({
        value,
        label: sourceLabel(value) || DEAL_SOURCE_FALLBACK[value],
      })),
    [sourceLabel],
  );

  const pickMatch = (match: LeadDuplicate) =>
    setPicked((p) =>
      match.kind === "contact"
        ? {
            ...p,
            contactId: match.id,
            companyId: p.companyId ?? match.company_id ?? null,
          }
        : { ...p, companyId: match.id },
    );

  const fillFromBrreg = (hit: BrregSearchHit) => {
    setValue("company_name", hit.navn, { shouldDirty: true });
    setValue("org_nr", hit.org_nr, { shouldDirty: true });
    setPicked((p) => ({ ...p, brreg: hit }));
  };

  return (
    <>
      <TextInput
        source="name"
        label={translate("crm.deals.lead.name", { _: "Navn" })}
        validate={required()}
        autoFocus
        helperText={false}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <TextInput
          source="phone"
          type="tel"
          inputMode="tel"
          label={translate("crm.deals.lead.phone", { _: "Telefon" })}
          helperText={false}
        />
        <TextInput
          source="email"
          type="email"
          inputMode="email"
          label={translate("crm.deals.lead.email", { _: "E-post" })}
          helperText={false}
        />
      </div>
      <div className="flex flex-col gap-2">
        <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
          <TextInput
            source="company_name"
            label={translate("crm.deals.lead.company", {
              _: "Firma (valgfritt)",
            })}
            helperText={false}
          />
          <TextInput
            source="org_nr"
            inputMode="numeric"
            label={translate("crm.deals.lead.org_nr", { _: "Org.nr." })}
            helperText={false}
          />
        </div>
        <BrregLookup
          query={(orgNr?.replace(/\s/g, "") || companyName || "").trim()}
          onPick={fillFromBrreg}
          picked={picked.brreg ?? null}
        />
      </div>
      <LeadDuplicatePanel
        matches={matches}
        hasInput={hasInput}
        isPending={isPending}
        onAddNote={(match) => onAddNote(match, getValues())}
        onUse={pickMatch}
        usedContactId={picked.contactId}
        usedCompanyId={picked.companyId}
      />
      <SelectInput
        source="need"
        label={translate("crm.deals.lead.need", { _: "Behov" })}
        choices={dealCategories}
        optionText="label"
        optionValue="value"
        helperText={false}
      />
      <SelectInput
        source="source"
        label={translate("crm.deals.lead.source_label", { _: "Kilde" })}
        choices={sourceChoices}
        optionText="label"
        optionValue="value"
        validate={required()}
        helperText={false}
      />
      {source === "referral" && (
        <ReferenceInput
          source="referred_by_id"
          reference="contacts_summary"
          {...KONTAKTVALG}
        >
          <AutocompleteInput
            label={translate("crm.deals.lead.referred_by", {
              _: "Anbefalt av",
            })}
            optionText={contactOptionText}
            helperText={false}
            modal
          />
        </ReferenceInput>
      )}
    </>
  );
};

// Oppslag i Brønnøysundregistrene (kap. 6.7, AK38): på firmanavn eller et
// org.nr på 9 siffer. Treffet fyller inn navn, org.nr. og adresse.
const BrregLookup = ({
  query,
  onPick,
  picked,
}: {
  query: string;
  onPick: (hit: BrregSearchHit) => void;
  picked: BrregSearchHit | null;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const [hits, setHits] = useState<BrregSearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const canSearch = query.length >= 3;

  const search = async () => {
    if (!canSearch) return;
    setLoading(true);
    try {
      const result = await dataProvider.searchBrreg(query);
      setHits(result);
      // Ett treff på org.nr. fylles inn med en gang.
      if (/^\d{9}$/.test(query) && result.length === 1) {
        onPick(result[0]);
        setHits(null);
      }
    } catch (error) {
      notify((error as Error)?.message || "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!canSearch || loading}
          onClick={search}
        >
          <Landmark className="h-4 w-4" />
          {translate("crm.deals.lead.brreg_search", {
            _: "Slå opp i Brønnøysund",
          })}
        </Button>
        {picked && (
          <span className="text-xs text-muted-foreground">
            {[picked.address, [picked.zipcode, picked.city].filter(Boolean)]
              .flat()
              .filter(Boolean)
              .join(" ")}
          </span>
        )}
      </div>
      {hits && (
        <ul
          aria-label={translate("crm.deals.lead.brreg_hits", {
            _: "Treff i Brønnøysund",
          })}
          className="flex flex-col divide-y rounded-md border text-sm"
        >
          {hits.length === 0 ? (
            <li className="p-2 text-muted-foreground">
              {translate("crm.deals.lead.brreg_none", { _: "Ingen treff" })}
            </li>
          ) : (
            hits.slice(0, 6).map((hit) => (
              <li key={hit.org_nr}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-2 py-1.5 text-left hover:bg-muted"
                  onClick={() => {
                    onPick(hit);
                    setHits(null);
                  }}
                >
                  <span className="font-medium">{hit.navn}</span>
                  <span className="text-xs text-muted-foreground">
                    {[`Org.nr. ${hit.org_nr}`, hit.city]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

// «+ Ny lead» på Pipeline. C trykker på knappen (CREATE_SHORTCUT_ATTR), så
// snarveien virker som «Ny …» ellers i Mimin.
export const NewLeadButton = () => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="c"
        {...{ [CREATE_SHORTCUT_ATTR]: "" }}
      >
        <Plus className="h-4 w-4" />
        {translate("crm.deals.new_lead", { _: "Ny lead" })}
      </Button>
      <LeadQuickCreate open={open} onOpenChange={setOpen} />
    </>
  );
};
